import * as React from 'react';
import * as z from 'zod';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';

import { Button } from '@/components/ui/button';
import { DropdownMenuItem } from '@/components/ui/dropdown-menu'; // Import DropdownMenuItem
import { Input } from '@/components/ui/input';
import { Pencil } from 'lucide-react';
import type { TFunction } from 'i18next'; // Import TFunction
import { toast } from 'sonner';
import { trpc } from '@/lib/trpc'; // Import trpc client
import { useForm } from 'react-hook-form';
import { useQueryClient } from '@tanstack/react-query'; // Keep queryClient
import { useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';

// Define the expected shape of the apiKey prop
type ApiKeyRow = {
  id: string;
  name: string;
};

// Define the props for the component
interface EditApiKeyDialogProps {
  apiKey: ApiKeyRow;
}

// Define schema using a function to access t
const getEditApiKeyFormSchema = (t: TFunction<"translation", undefined>) => z.object({
  name: z.string()
    .min(1, { message: t('settings.apiKeys.dialog.edit.errors.nameEmpty', 'API Key name cannot be empty.') })
    .max(100, { message: t('settings.apiKeys.dialog.edit.errors.nameTooLong', 'Name cannot exceed 100 characters.') }),
});

// Infer type from the schema function's return type
type FormData = z.infer<ReturnType<typeof getEditApiKeyFormSchema>>;

export const EditApiKeyDialog: React.FC<EditApiKeyDialogProps> = ({ apiKey }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = React.useState(false); // Manage dialog state internally

  // Get the schema with translations
  const schema = getEditApiKeyFormSchema(t);

  const form = useForm<FormData>({
    resolver: zodResolver(schema), // Use the translated schema
    defaultValues: {
      name: apiKey.name,
    },
  });

  React.useEffect(() => {
    form.reset({ name: apiKey.name });
  }, [apiKey, form]);

  // Use the new tRPC mutation hook
  const updateMutation = trpc.settings.updateApiKey.useMutation({
    onSuccess: () => {
      toast.success(t('settings.apiKeys.editSuccessToast', 'API Key name updated successfully.'));
      queryClient.invalidateQueries({ queryKey: ['apiKeys'] }); // Corrected query key
      queryClient.invalidateQueries({ queryKey: ['apiKeyDetails', apiKey.id] });
      setIsOpen(false); // Close dialog on success
      form.reset();
    },
    onError: (error) => {
      toast.error(error.message || t('settings.apiKeys.editErrorToast', 'Failed to update API Key name'));
    },
  });


  const onSubmit = (values: FormData) => {
    // Pass the required input to the mutation
    updateMutation.mutate({
      id: apiKey.id, // Pass the id
      name: values.name,
    });
  };

  // Handle open state changes, reset form if dialog is closed externally
  const handleOpenChange = (open: boolean) => {
    if (!open) {
      form.reset({ name: apiKey.name });
    }
    setIsOpen(open);
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <DropdownMenuItem
          onSelect={(e) => {
            e.preventDefault();
            setIsOpen(true);
          }}
        >
          <Pencil className="mr-2 h-4 w-4" />
          <span>{t('settings.apiKeys.editAction', 'Edit')}</span>
        </DropdownMenuItem>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{t('settings.apiKeys.editDialogTitle', 'Edit API Key Name')}</DialogTitle>
          <DialogDescription>
            {t('settings.apiKeys.editDialogDescription', 'Update the name for this API key.')}
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 py-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('settings.apiKeys.dialog.edit.nameLabel', 'Key Name')}</FormLabel>
                  <FormControl>
                    <Input placeholder={t('settings.apiKeys.dialog.edit.namePlaceholder', 'Enter key name')} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
                {t('common.cancel', 'Cancel')}
              </Button>
              <Button type="submit" disabled={updateMutation.isPending || !form.formState.isDirty}>
                {updateMutation.isPending ? t('common.saving', 'Saving...') : t('common.save', 'Save Changes')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};